import React, { ChangeEvent } from "react";

import {
  Button,
  InfoPanel,
  TextField,
  SelectData,
  Select,
  CardListItem,
} from "@king-ict/eupisi-ui/components";
import { SelectEvent } from "@king-ict/eupisi-ui/components";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { getIntegrationBenefitDetailsData as getIntegrationBenefitDetailsDataApi } from "../../../api/kindergartenApi";
import BenefitDataFetchStatusEnum from "../../../models/Benefit/BenefitDataFetchStatusEnum";
import BenefitIntegrationPersonData from "../../../models/Benefit/BenefitIntegrationPersonData";
import RequestBenefitDetails from "../../../models/Request/RequestBenefitDetails";
import RequestBenefitDetailsValidationSchema from "../../../models/Request/RequestBenefitDetailsValidationSchema";
import CodeBook from "../../../models/Shared/CodeBook";
import styles from "./BenefitDetailsItem.module.scss";

interface Props {
  relationshipsData: CodeBook[];
  benefitDetails: RequestBenefitDetails;
  institutionBenefitId: number;
  benefitDetailsLength: number;
  integrationName: string;
  handleRemoveMember: (id: number, institutionBenefitId: number) => void;
  handleDetailsChange: (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >,
    institutionBenefitId: number,
    detailsId: number
  ) => void;
  handleGetBenefitDetailsIntegrationData: (
    data: BenefitIntegrationPersonData,
    institutionBenefitId: number,
    detailsId: number
  ) => void;
  isSent: boolean;
}

export default function BenefitDetailsItem({
  relationshipsData,
  benefitDetails,
  institutionBenefitId,
  benefitDetailsLength,
  integrationName,
  handleRemoveMember,
  handleDetailsChange,
  handleGetBenefitDetailsIntegrationData,
  isSent,
}: Props) {
  const { t } = useTranslation();

  const [errors, doValidations] = useValidation({
    data: benefitDetails,
    validationSchema: RequestBenefitDetailsValidationSchema,
  });

  const validate = () => {
    const isValid = doValidations();

    if (isValid) {
      getIntegrationBenefitDetailsDataApi({
        benefitId: institutionBenefitId,
        oib: benefitDetails.oib,
        firstName: benefitDetails.firstName,
        lastName: benefitDetails.lastName,
        relationshipId: benefitDetails.relationshipId!,
      }).then((res) => {
        handleGetBenefitDetailsIntegrationData(
          res,
          institutionBenefitId,
          benefitDetails.id
        );
      });

      return true;
    } else {
      return isValid;
    }
  };

  return (
    <CardListItem
      isDraggable={false}
      itemKey={`${benefitDetails.id}_ ${institutionBenefitId}`}
      color="grey"
      key={`${benefitDetails.id}_ ${institutionBenefitId}`}
      className={styles.cardListItem}
    >
      <Row>
        <Col md={3}>
          <TextField
            disabled={isSent}
            required
            type="text"
            name={nameof<typeof benefitDetails>("oib")}
            label={t("person.oib")}
            value={benefitDetails.oib || ""}
            onChange={(e) =>
              handleDetailsChange(e, institutionBenefitId, benefitDetails.id)
            }
            errorText={errors && errors?.oib}
          />
        </Col>
        <Col md={3}>
          <TextField
            disabled={isSent}
            required
            type="text"
            name={nameof<typeof benefitDetails>("firstName")}
            label={t("person.firstName")}
            value={benefitDetails.firstName || ""}
            onChange={(e) =>
              handleDetailsChange(e, institutionBenefitId, benefitDetails.id)
            }
            errorText={errors && errors?.firstName}
          />
        </Col>
        <Col md={3}>
          <TextField
            disabled={isSent}
            required
            type="text"
            name={nameof<typeof benefitDetails>("lastName")}
            label={t("person.lastName")}
            value={benefitDetails.lastName || ""}
            onChange={(e) =>
              handleDetailsChange(e, institutionBenefitId, benefitDetails.id)
            }
            errorText={errors && errors?.lastName}
          />
        </Col>
        <Col md={3}>
          <Select
            disabled={isSent}
            required
            label={t("person.relationship")}
            name={nameof<typeof benefitDetails>("relationshipId")}
            value={benefitDetails.relationshipId || undefined}
            onChange={(e) =>
              handleDetailsChange(e, institutionBenefitId, benefitDetails.id)
            }
            selectData={
              {
                valueProp: "id",
                textProp: "name",
                data: relationshipsData || [],
              } as SelectData<CodeBook>
            }
            errorText={errors && errors?.relationshipId}
          />
        </Col>
        <Col md={6}>
          {benefitDetails.fetchedIntegrationData || isSent ? (
            <InfoPanel
              message={
                benefitDetails.integrationData?.fetchStatus ===
                BenefitDataFetchStatusEnum.Success
                  ? t("benefit.criterionSystemInformation", {
                      system: integrationName,
                    })
                  : benefitDetails.integrationData?.fetchStatus ===
                    BenefitDataFetchStatusEnum.DataMismatch
                  ? t("benefit.checkDataMismatchMessage")
                  : benefitDetails.integrationData?.fetchStatus ===
                    BenefitDataFetchStatusEnum.OibFailed
                  ? t("benefit.checkDataOibFailedMessage")
                  : t("benefit.failIntegration")
              }
              color={
                benefitDetails.integrationData?.fetchStatus ===
                BenefitDataFetchStatusEnum.Success
                  ? "success"
                  : benefitDetails.integrationData?.fetchStatus ===
                    BenefitDataFetchStatusEnum.Fail
                  ? "danger"
                  : "warning"
              }
            />
          ) : null}
        </Col>
        <Col md={3}>
          <div className={`${styles.textButton}`}>
            {benefitDetailsLength > 1 && !isSent && (
              <Button
                outline
                boxless
                size="sm"
                onClick={() =>
                  handleRemoveMember(institutionBenefitId, benefitDetails.id)
                }
              >
                {t("benefit.removeMember")}
              </Button>
            )}
          </div>
        </Col>
        <Col md={3}>
          {!isSent && (
            <div className={`${styles.textButton}`}>
              <Button
                size="sm"
                onClick={() => {
                  validate();
                }}
                disabled={
                  benefitDetails.fetchedIntegrationData &&
                  benefitDetails.integrationData?.fetchStatus ===
                    BenefitDataFetchStatusEnum.Success
                }
              >
                {t("benefit.getData")}
              </Button>
            </div>
          )}
        </Col>
      </Row>
    </CardListItem>
  );
}
